import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, AlertTriangle, Loader2 } from "lucide-react";

export default function ForecastCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/forecast/cashflow?days=90").then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return (
    <div className="glass-card p-6 flex items-center justify-center h-48">
      <Loader2 className="w-6 h-6 animate-spin text-[color:var(--gold)]" />
    </div>
  );

  const pts = data.points;
  const values = pts.map(p => p.balance);
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const W = 800, H = 160;
  const step = W / (values.length - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(H - ((v - min) / range) * H).toFixed(1)}`).join(" ");
  const zeroY = min < 0 ? H - ((0 - min) / range) * H : null;
  const trending = values[values.length - 1] > values[0];

  return (
    <div data-testid="forecast-card" className="glass-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-1">90-Day Cash Flow Forecast</div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-2xl font-bold gold-gradient-text">${data.current_cash.toLocaleString()}</span>
            <span className="text-xs text-[color:var(--text-secondary)]">now · projected ~${Math.round(values[values.length - 1]).toLocaleString()} in 90d</span>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full ${trending ? "bg-[rgba(42,157,143,0.15)] text-[color:var(--emerald)]" : "bg-[rgba(224,106,59,0.15)] text-[color:var(--terracotta)]"}`}>
          {trending ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          ${Math.abs(data.daily_net).toFixed(0)}/day {trending ? "up" : "down"}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
        <defs>
          <linearGradient id="fc-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        {zeroY !== null && <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="#E06A3B" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />}
        <polygon fill="url(#fc-grad)" points={`0,${H} ${points} ${W},${H}`} />
        <polyline fill="none" stroke="#D4AF37" strokeWidth="2" points={points} />
      </svg>

      <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
        <div><div className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Min</div><div className="font-mono font-semibold">${Math.round(data.min_projected).toLocaleString()}</div></div>
        <div><div className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Max</div><div className="font-mono font-semibold">${Math.round(data.max_projected).toLocaleString()}</div></div>
        <div><div className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Recurring bills</div><div className="font-mono font-semibold">{data.recurring_count} detected</div></div>
      </div>

      {data.first_negative_date && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="mt-4 p-3 rounded-xl bg-[rgba(224,106,59,0.08)] border border-[rgba(224,106,59,0.25)] flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-[color:var(--terracotta)] mt-0.5 shrink-0" />
          <div className="text-xs text-[color:var(--text-primary)]"><span className="font-semibold text-[color:var(--terracotta)]">Warning:</span> Cash projected to go negative around <span className="font-mono">{data.first_negative_date}</span>. Consider trimming discretionary spend or increasing income before then.</div>
        </motion.div>
      )}
    </div>
  );
}
